import { Injectable } from '@angular/core';

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';

import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  private apiBaseURL: string = '';
  private loading: boolean = false;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private router: Router
  ) {
    this.apiBaseURL = environment.api_url;
  }

  public getApiBaseURL() {
    return this.apiBaseURL;
  }

  public async get(endpoint: string, params: any = {}) {
    let httpHeaders = new HttpHeaders()
      .set('Content-Type', 'application/x-www-form-urlencoded')
      .set('Authorization', "Bearer " + this.authService.getToken());

    try {
      return new Promise((resolve, reject) => {
        let apiURL = this.getApiBaseURL() + endpoint;

        // Convert params to URL query string
        if (Object.keys(params).length > 0) {
          const searchParams = new URLSearchParams();
          Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              searchParams.append(key, String(value)); // Ensure all values are strings
            }
          });
          if (apiURL.includes('?')) {
            apiURL += `&${searchParams.toString()}`;
          } else {
            apiURL += `?${searchParams.toString()}`;
          }
        }

        this.http.get(apiURL, { headers: httpHeaders })
          .toPromise()
          .then(res => resolve(res))
          .catch(err => {
            if (err.status === 403 && !window.location.pathname.includes('/login')) {
              window.location.href = '/login';
              return;
            }
            reject(err);
          });
      });
    } finally {
      // Clean-up or additional processing if needed
    }
  }

  public async getText(endpoint: string, params: any = {}) {
    const httpHeaders = new HttpHeaders()
      .set('Authorization', "Bearer " + this.authService.getToken());

    return new Promise((resolve, reject) => {
      let apiURL = this.getApiBaseURL() + endpoint;

      if (Object.keys(params).length > 0) {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            searchParams.append(key, String(value));
          }
        });
        apiURL += apiURL.includes('?') ? `&${searchParams.toString()}` : `?${searchParams.toString()}`;
      }

      this.http.get(apiURL, { headers: httpHeaders, responseType: 'text' })
        .toPromise()
        .then(res => resolve(res))
        .catch(err => {
          if (err.status === 403 && !window.location.pathname.includes('/login')) {
            window.location.href = '/login';
            return;
          }
          reject(err);
        });
    });
  }

  public async getImage(endpoint: string, params: any = {}) {
    const httpHeaders = new HttpHeaders()
      .set('Authorization', "Bearer " + this.authService.getToken());

    return new Promise((resolve, reject) => {
      let apiURL = this.getApiBaseURL() + endpoint;

      if (Object.keys(params).length > 0) {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            searchParams.append(key, String(value));
          }
        });
        apiURL += apiURL.includes('?') ? `&${searchParams.toString()}` : `?${searchParams.toString()}`;
      }

      this.http.get(apiURL, { headers: httpHeaders, responseType: 'blob' })
        .toPromise()
        .then(blob => resolve(blob))
        .catch(err => {
          if (err.status === 403 && !window.location.pathname.includes('/login')) {
            window.location.href = '/login';
            return;
          }
          reject(err);
        });
    });
  }

  public post(endpoint: string, data: any) {
    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders.set('Authorization', "Bearer " + this.authService.getToken());

    try {
      return new Promise((resolve, reject) => {
        const apiURL = this.getApiBaseURL() + endpoint;

        const formData = new FormData();

        for (const key in data) {
          if (data.hasOwnProperty(key)) {
            formData.append(key, data[key]);
          }
        }

        this.http.post(apiURL, formData, {
          headers: httpHeaders
        })
          .toPromise()
          .then(res => {
            resolve(res);
          })
          .catch(err => {
            if (err.status === 401 && !window.location.pathname.includes('/login')) {
              window.location.href = '/login';
              return;
            }
            reject(err);
          });
      });
    } finally {
    }
  }


  public patch(endpoint: string, data: any, skipAuth: boolean = false) {
    let httpHeaders = new HttpHeaders();

    httpHeaders = httpHeaders.set('Authorization', "Bearer " + this.authService.getToken());

    try {
      return new Promise((resolve, reject) => {
        let apiURL = this.getApiBaseURL() + endpoint;
        this.http.patch(apiURL, data, {
          headers: httpHeaders
        })
          .toPromise()
          .then(
            res => {
              resolve(res);
            }
          )
          .catch((err) => {
            if (!skipAuth && err.status === 403 && !window.location.pathname.includes('/login')) {
              window.location.href = '/login';
              return;
            }

            reject(err);
          });
      });
    } finally {
    }
  }

  public put(endpoint: string, data: any, skipAuth: boolean = false) {
    let httpHeaders = new HttpHeaders();

    httpHeaders = httpHeaders.set('Authorization', "Bearer " + this.authService.getToken());

    try {
      return new Promise((resolve, reject) => {
        let apiURL = this.getApiBaseURL() + endpoint;
        this.http.put(apiURL, data, {
          headers: httpHeaders
        })
          .toPromise()
          .then(
            res => {
              resolve(res);
            }
          )
          .catch((err) => {
            if (!skipAuth && err.status === 403 && !window.location.pathname.includes('/login')) {
              window.location.href = '/login';
              return;
            }

            reject(err);
          });
      });
    } finally {
    }
  }

  public delete(endpoint: string, skipAuth: boolean = false) {
    let httpHeaders = new HttpHeaders()
      .set('Content-Type', 'application/x-www-form-urlencoded');

    httpHeaders = httpHeaders.set('Authorization', "Bearer " + this.authService.getToken());

    try {
      return new Promise((resolve, reject) => {
        let apiURL = this.getApiBaseURL() + endpoint;
        this.http.delete(apiURL, {
          headers: httpHeaders
        })
          .toPromise()
          .then(
            res => {
              resolve(res);
            }
          ).catch((err) => {
            if (!skipAuth && err.status === 403 && !window.location.pathname.includes('/login')) {
              window.location.href = '/login';
              return;
            }

            reject(err);
          });
      });
    } finally {
    }
  }
}
